// Deep import, not the feature barrel: `@/features/system-modules` re-exports ./pages, which would
// drag SystemModulesPage into the import graph of a low-level stream lib (and a cycle risk with it).
import { useSystemModuleStore } from '@/features/system-modules/store/systemModuleStore';
import {
  openDocumentCaptureStream,
  type DocStreamConnectionState,
  type DocStreamHandle,
} from './documentCaptureStream';
import type { DocStreamEvent } from '../types/documentCapture.types';

/**
 * Ref-counted fan-out over the ONE Document Capture event stream (86d3w2jrj FE-3).
 *
 * The stream existed before this, but exactly one component consumed it — the capture page — so
 * every other surface went stale the moment it opened. A reviewer working the queue, or anyone
 * sitting on `/d/{docNumber}`, saw whatever was true when they arrived: a decode finishing, a
 * replacement being approved by a colleague, a link landing in NetSuite, all invisible until a
 * manual refresh. That staleness is the problem this feature actually has (concurrency is low), and
 * the events describing every one of those changes were already arriving.
 *
 * Ref-counted rather than one connection per subscriber because EventSource connections are a
 * scarce, per-origin browser resource (~6 on HTTP/1.1) and the server holds one open request per
 * connection. The count also survives React StrictMode's double-mount and the
 * `/documents` → `/d/:docNo` navigation, where an unmount and a mount interleave: the underlying
 * EventSource is opened on 0→1 and closed on 1→0, so a handover never tears it down and rebuilds it.
 *
 * Subscribers are called in registration order and are isolated — one throwing handler must not
 * stop the others from seeing the event, or a bug in a detail panel silently freezes the whole page.
 */

type EventHandler = (event: DocStreamEvent) => void;
type StateHandler = (state: DocStreamConnectionState) => void;

const eventHandlers = new Set<EventHandler>();
const stateHandlers = new Set<StateHandler>();

let handle: DocStreamHandle | null = null;
let lastState: DocStreamConnectionState = 'connecting';

/**
 * 86d3y0cku — the tab woke up or the network came back. Only meaningful once we have given up; in
 * every other state the stream is already connected or already has a reconnect scheduled.
 *
 * This is what makes a bounded reconnect budget safe. The budget outlasts a deploy, but if it does
 * run out during a genuine outage the user is not stranded: switching back to the tab, or the
 * browser regaining connectivity, buys a fresh attempt for free. A tab sitting idle in the
 * background — the exact case that produced the retry storm — asks for nothing at all.
 */
function handleWakeSignal() {
  if (lastState !== 'offline') return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  handle?.retryNow();
}

function ensureOpen() {
  if (handle) return;
  handle = openDocumentCaptureStream({
    onEvent: (event) => {
      for (const fn of [...eventHandlers]) {
        try {
          fn(event);
        } catch (err) {
          console.warn('doc-capture subscriber threw while handling an event:', err);
        }
      }
    },
    onConnectionStateChange: (state) => {
      lastState = state;
      for (const fn of [...stateHandlers]) {
        try {
          fn(state);
        } catch (err) {
          console.warn('doc-capture subscriber threw while handling a state change:', err);
        }
      }
    },
    // The stream ran out of reconnects. It cannot see WHY — EventSource surfaces no status code — so
    // ask the one endpoint that can answer: module availability. It is permission-free and marked
    // `background`, so it never toasts. Two outcomes, both correct without further branching here:
    //   • DSCN is disabled → the store's `modules` updates → ModuleGuard re-renders (it subscribes to
    //     that array) and swaps the page for its notice → the last subscriber unmounts → closeIfIdle()
    //     closes this handle. Total cost of a disabled window, per tab: the reconnect budget plus ONE
    //     request, then silence. That is the whole point of this ticket.
    //   • The API is simply down → this call fails too and the store fails open, leaving `modules`
    //     untouched. The page stays, honestly reporting 'offline', with the wake signals and the
    //     panel's Reconnect button as the way back.
    onGaveUp: () => void useSystemModuleStore.getState().fetchAvailability(),
  });

  if (typeof window !== 'undefined') {
    window.addEventListener('online', handleWakeSignal);
    document.addEventListener('visibilitychange', handleWakeSignal);
  }
}

function closeIfIdle() {
  if (eventHandlers.size > 0 || stateHandlers.size > 0) return;
  if (typeof window !== 'undefined') {
    window.removeEventListener('online', handleWakeSignal);
    document.removeEventListener('visibilitychange', handleWakeSignal);
  }
  handle?.close();
  handle = null;
  lastState = 'connecting';
}

/**
 * Subscribe to stream events. Returns the unsubscribe function; the connection closes when the last
 * subscriber leaves.
 *
 * `onConnectionState` is optional and is called immediately with the current state, so a late
 * subscriber does not have to wait for the next transition to render an honest connection chip.
 */
export function subscribeDocCapture(
  onEvent: EventHandler,
  onConnectionState?: StateHandler
): () => void {
  eventHandlers.add(onEvent);
  if (onConnectionState) {
    stateHandlers.add(onConnectionState);
    if (handle) onConnectionState(lastState);
  }
  ensureOpen();

  return () => {
    eventHandlers.delete(onEvent);
    if (onConnectionState) stateHandlers.delete(onConnectionState);
    closeIfIdle();
  };
}

/**
 * Manual retry after the reconnect budget ran out (86d3y0cku) — wired to the Reconnect button the
 * live panel shows in the 'offline' state. A no-op when nothing is subscribed, and harmless while
 * the stream is healthy (it just re-opens on a fresh budget).
 */
export function reconnectDocCapture(): void {
  handle?.retryNow();
}

/** Test/diagnostic seam — how many live subscribers the bus is fanning out to. */
export function docCaptureSubscriberCount(): number {
  return eventHandlers.size;
}
